import {SessionLogsProvider} from '@github-ui/agent-sessions/components/session-logs/Context'
import {Tool} from '@github-ui/agent-sessions/components/session-logs/tool/ToolPrimitives'
import {useChatMessage} from '@github-ui/copilot-chat/components/ChatMessageContext'
import {ReferencesList} from '@github-ui/copilot-chat/components/ChatReferences'
import {metadataFromFunctionCall} from '@github-ui/copilot-chat/components/FunctionLoadingUtils'
import {getRenderableReferences} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import type {SkillExecution} from '@github-ui/copilot-chat-types'
import {WithShimmerEffect} from '@github-ui/copilot-chat-visuals/WithShimmerEffect'
import {clsx} from 'clsx'
import {useMemo, useState} from 'react'

import type {ToolLogEntry} from './ToolCallHeader'
import {ToolCallHeader} from './ToolCallHeader'
import styles from './ToolCallLogs.module.css'

interface ToolCallLogsProps {
  waitingForCopilotContent: boolean | undefined
  currentSkillExecution: SkillExecution | undefined
  isAssistive?: boolean
}

export function ToolCallLogs({waitingForCopilotContent, currentSkillExecution, isAssistive}: ToolCallLogsProps) {
  const {message} = useChatMessage()
  const hasSkillExecutions = (message.skillExecutions?.length ?? 0) > 0
  const [isOpen, setIsOpen] = useState(false)

  const contentId = 'tool-call-logs-content'

  const toolCallsMetadata = useMemo(() => {
    const metadataMap = new Map<string, ToolLogEntry>()

    for (const skillExecution of message.skillExecutions ?? []) {
      const functionMetadata = metadataFromFunctionCall(skillExecution)
      let logMessage = ''
      if (functionMetadata) {
        if (skillExecution.status === 'started' || skillExecution.status === 'progress') {
          logMessage = functionMetadata.loadingText
        } else if (skillExecution.status === 'completed') {
          logMessage = functionMetadata.completedText
        } else if (skillExecution.status === 'error') {
          logMessage = `${functionMetadata.completedText} completed with errors.`
        }
        const toolLogEntry: ToolLogEntry = {
          ...functionMetadata,
          logMessage,
          status: skillExecution.status,
          references: skillExecution.references ? getRenderableReferences(skillExecution.references) : undefined,
        }
        if (functionMetadata.callId) {
          // Updated behaviour to use callId as the key so that multiple calls to the same tool are logged separately
          metadataMap.set(functionMetadata.callId, toolLogEntry)
        } else {
          // Fallback to using function id as the key for backwards compatibility
          // TODO - remove in 30 days once older messages are deleted from history
          metadataMap.set(functionMetadata.id, toolLogEntry)
        }
      }
    }

    return Array.from(metadataMap.values())
  }, [message.skillExecutions])

  const handleClick = () => {
    setIsOpen(prevIsOpen => !prevIsOpen)
  }

  // Render loading text if skill executions haven't started yet
  if (!hasSkillExecutions && waitingForCopilotContent) {
    return (
      <WithShimmerEffect className={clsx(styles.loadingText, isAssistive && styles.assistive)}>
        Thinking...
      </WithShimmerEffect>
    )
  }

  // Don't render anything if there are no skill executions and we're not waiting
  if (!hasSkillExecutions) {
    return null
  }

  return (
    <div className={styles.detailsContainer} data-active={!!waitingForCopilotContent} data-session-id={contentId}>
      <ToolCallHeader
        isOpen={isOpen}
        handleClick={handleClick}
        waitingForCopilotContent={!!waitingForCopilotContent}
        toolCallsMetadata={toolCallsMetadata}
        currentSkillExecution={currentSkillExecution}
        contentId={contentId}
      />
      <div id={contentId} className={styles.contentContainer} data-expanded={isOpen} data-testid={contentId}>
        <div className={styles.logsContainer}>
          <SessionLogsProvider variant={'chat'}>
            {toolCallsMetadata.map(toolLog => (
              <Tool
                key={toolLog.callId || toolLog.id}
                title={toolLog.logMessage}
                isError={toolLog.status === 'error'}
                isPending={toolLog.status === 'started' || toolLog.status === 'progress'}
              >
                {toolLog.references && toolLog.references.length > 0 && (
                  <ReferencesList references={toolLog.references} isImmersive />
                )}
              </Tool>
            ))}
          </SessionLogsProvider>
        </div>
      </div>
    </div>
  )
}
